import {AbstractControl} from '@angular/forms';
import {Address, AnyMap} from './interfaces';

export type ValidatorType = (control:AbstractControl) => AnyMap;

const TOP_NAVBAR_HEIGHT = 83;
const LOCAL_DATE_TIME_FORMAT = new Intl.DateTimeFormat('en-US', {
    hour12: true, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: 'numeric', minute: '2-digit', second: '2-digit'
});

export class Utils {
    /** Compares two arbitrary values, using deep comparison as needed, taking into account arrays and plain objects.
     *
     * @param obj1 {any} first item to compare
     * @param obj2 {any} second item to compare
     * @return {boolean} true if equal, false otherwise
     */
    static DeepEquals(obj1: any, obj2: any): boolean {
        if (!obj1 && obj2 || obj1 && !obj2) {
            return false;
        }
        if (!obj1 && !obj2) {
            return typeof obj1 == typeof obj2;
        }
        if (typeof obj1 != typeof obj2) {
            return false;
        }
        if (typeof obj1 == "object" && typeof obj2 == "object") {
            const firstIsArray = obj1.constructor === Array;
            const secondIsArray = obj2.constructor === Array;
            if (firstIsArray != secondIsArray) {
                return false;
            }
            if (firstIsArray) {
                if (obj1.length != obj2.length)
                    return false;
                return obj1.every((item: any, index: number) => {
                    return Utils.DeepEquals(item, obj2[index]);
                });
            } else {
                const keys1 = Object.keys(obj1);
                const keys2 = Object.keys(obj2);
                if (keys1.length != keys2.length)
                    return false;
                return keys1.every((key:any) => {
                    return Utils.DeepEquals(obj1[key], obj2[key]);
                });
            }
        }
        return obj1 === obj2;
    }

    /** Creates and returns a deep clone of the arbitrary given object.
     *
     * @param oldObj {any} object to be cloned
     * @returns {any} the cloned copy
     */
    static DeepClone(oldObj: any): any {
        let newObj = oldObj;
        if (oldObj && typeof oldObj === "object") {
            if (oldObj.constructor === Array) {
                newObj = oldObj.map((x: any) => Utils.DeepClone(x));
            } else {
                newObj = {};
                Object.keys(oldObj).forEach(x => newObj[x] = Utils.DeepClone(oldObj[x]));
            }
        }
        return newObj;
    }

    /** Creates and returns an array of integers from 0 to count-1 or from first to last (inclusive)
     *
     * @param countOrFirst if last is undefined, this is the total number of items in the array, otherwise its the value
     *              of the first number in the range
     * @param last (optional) if not undefined, the last number in the generated range. Should be last >= countOrFirst
     * @returns {Array} the generated range array
     */
    static Range(countOrFirst:number, last?:number): number[] {
        const count: number = last === undefined? countOrFirst : last - countOrFirst + 1;
        const first: number = last === undefined? 0 : countOrFirst;
        return Array.from(Array(count)).map((k) => first + k);
    }

    /** Takes a number and converts it to a string that has at least the requested number of digits. If shorter, it pads it on the left
     * with zeroes.
     *
     * @param n
     * @param desiredLength
     * @return the zero-padded string representation of the number
     */
    static ZeroPad(n: number, desiredLength: number): string {
        const sn: string = `${n}`;
        if (sn.length >= desiredLength)
            return sn;
        let nPad = desiredLength - sn.length;
        let out = sn;
        while (nPad--)
            out = `0${out}`;
        return out;
    }

    /** Similar to element's scrollIntoView (with behavior: 'smooth') for an element, but taking into account
     * the ever present sticky navbar that takes up a section at the top of the screen.
     *
     * @param elem the element to scroll
     * @param verticalOffset optional extra offset to scroll the element below the top of the visible area.
     */
    static ScrollIntoView(elem: HTMLElement, verticalOffset: number = 0) {
        // Determine element's top coordinate relative to window
        if (window) {
            const elemTop = elem.getBoundingClientRect().top + window.pageYOffset;
            // Scroll to that position minus the sticky menu height
            window.scroll({top: elemTop - TOP_NAVBAR_HEIGHT + verticalOffset, left: 0, behavior: 'smooth'});
        }
    }

    /** Useful for debugging custom validators. Wraps a validator so it can log what it sees.
     *
     * @param validator
     * @param desc
     * @return the custom validator function
     */
    static Peek(validator: ValidatorType, desc: string): ValidatorType {
        return (control:AbstractControl) => {
            const out = validator(control);
            console.log("validator:", desc, ", result:", out);
            return out;
        };
    }

    static FormatAddress(a: Address): string {
        let out: string = `${a.name}<br/>${a.street}`;
        if (a.street_2)
            out += `, ${a.street_2}`;
        out += `<br/>${a.city}, ${a.state} ${a.zip}<br/>${Utils.FormatPhone(a.phone)}`;
        return out;
    }

    /** Formats a phone number, expected to consist of exactly 10 digits with any other types of characters in between, returning it
     * in the format nnn-nnn-nnnn, or just the plain 10 digits if raw format is requested
     *
     * @param phone original phone string to format
     * @param asRaw whether to return just the raw digits
     * @return the formatted phone number
     */
    static FormatPhone(phone:string, asRaw:boolean=false): string {
        if (!phone)
            return phone;
        const parts = phone.match(/\d+/g);
        const digits = parts.join("");
        if (asRaw)
            return digits;
        let m = digits.match(/^(\d{3})(\d{3})(\d{4})$/);
        return !m ? phone : [m[1], m[2], m[3]].join("-");
    }

    /** Given a dollar amount, it returns the corresponding value rounded to 2 decimal digits places
     *
     * @param dollarAmount the amount to round
     * @return the rounded value
     */
    static RoundToCents(dollarAmount: number): number {
        return Math.round(dollarAmount*100)/100;
    }


    /** Given a timestamp in string form or as a Date instance, returns a string with the same info formatted
     * in the standard US format: mm/dd/yyyy hh:mm:ss ampm
     *
     * @param timestamp
     * @return {string} timestamp properly formatted
     */
    static AsUsTimestamp(timestamp:string|Date): string {
        const d = timestamp instanceof Date? timestamp : new Date(timestamp);
        return LOCAL_DATE_TIME_FORMAT.format(d);
    }

    static AsIsoDate(timestamp: string|Date): string {
        const dateOpts = {year: 'numeric', month: '2-digit', day: '2-digit'};
        return new Date(timestamp).toLocaleString('sv-SE', dateOpts);
    }

    static AsIsoTimestamp(timestamp: string|Date, withSeconds:boolean=false): string {
        const timestampOpts = {year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'};
        if (withSeconds)
            timestampOpts['second'] = '2-digit';
        return new Date(timestamp).toLocaleString('sv-SE', timestampOpts).replace(' ', 'T');
    }
}
